<script>
  import { afterUpdate, onMount } from "svelte"
  import { copyToClipboard } from "../../../../SinglePublish/script"
  import notify from "../../../../../../Script/notify"
  import bulkJson from "../../../Stores/jsonBulkData"
  import { createEventDispatcher } from "svelte"
  import Loader from "../../../../../../Shared/Slots/loader.svelte"
  import { loadTooltip } from "../../../../../../Script/loadTooltip"
  const dispatch = createEventDispatcher()

  /**
   * @type {boolean} whether to disable release btn or not during document release
   */
  export let disableReleaseBtn
  /**
   * @type {import("../../../Script/document".bulkDocument)}
   */
  export let document
  /**
   * @type {string} text to be shown in the card
   */
  let cardText
  /**
   * @type {string} The hexadecimal color code representing a specific card color.
   */
  let cardColor
  /**
   * @type {string} The hexadecimal color code representing a specific text color.
   */
  let textColor
  /**
   * @type {Boolean} whether to show or hide the copy text
   */
  let popover = false
  /**
   * @type {boolean} whether to show loading screen or not
   */
  let showLoading = false

  onMount(() => {
    changeColorAndText()
  })
  afterUpdate(() => {
    changeColorAndText()
  })

  /**
   * Change the card color and text based on the document's status.
   * This function updates the visual representation of a document card.
   */
  const changeColorAndText = () => {
    // Check if the document is marked as failed
    if (document.isDocumentFailed) {
      // Set card color and text for a failed document
      cardColor = "#f8d7da"
      textColor = "#721c24"
    } else {
      if (document.txidPublished) {
        // Set card color and text to indicate a successfully published document
        cardColor = "#C2FFE9"
        textColor = "#005620"
        return
      } else {
        // Check if the document has a signature
        if (!document.signature) {
          // Set card text to indicate waiting for signature generation
          cardText = "Waiting for Signature generation..."
          cardColor = "#F9E0C2"
          textColor = "#EA9633"
          return
        } else {
          // Set card text to indicate publishing to the blockchain
          cardText = "Publishing to BlockChain..."
          cardColor = "#CCEAE8"
          textColor = "#1F51FF"
          return
        }
      }
    }
  }

  /**
   * Copies the document URL to the clipboard and displays a popover notification.
   * Uses a try-catch block to handle errors.
   */
  const copyURL = async () => {
    try {
      // Copy the document URL to the clipboard using the 'copyToClipboard' function
      await copyToClipboard(document.url)

      // Set 'popover' to true to show the popover notification
      popover = true

      // Automatically hide the popover after 2000 milliseconds (2 seconds)
      setTimeout(() => {
        popover = false
      }, 500)
    } catch (error) {
      const info = "PAGE: Sign and Publish (Bulk Upload)\nEVENT: While copying document URL after publish"
      // If an error occurs during copying, show a danger notification
      notify.danger("There's an issue in copying text", info)
    }
  }

  /**
   * Function for releasig the document
   */
  const releaseDocument = async () => {
    try {
      // Set showLoading and disableReleaseBtn to true to indicate loading state
      showLoading = true
      disableReleaseBtn = true

      // Call the releaseSelectedDocument function with documentData.id
      await releaseSelectedDocument(document.id)

      // Get the receiver's name from document
      let receiver = document.recName

      // Remove the released document from the $bulkJson array
      $bulkJson = $bulkJson.filter((a) => a.id != document.id)

      // Display a success notification indicating the successful release
      notify.success(`Document of <strong>${receiver}</strong> released Successfully`)

      // Dispatch an event named "release"
      dispatch("release")
    } catch (error) {
      const info = "PAGE: Sign and Publish (Bulk Upload)\nEVENT: While releasing document"
      notify.danger(error, info)
    } finally {
      // Set showLoading and disableReleaseBtn back to false to end the loading state
      showLoading = false
      disableReleaseBtn = false
    }
  }

  afterUpdate(() => {
    loadTooltip()
    window.addEventListener("resize", loadTooltip)
  })
</script>

<div style="background: {cardColor};color: {textColor}" class="relative flex w-full items-center gap-3 rounded-md px-2 py-3 text-base font-medium">
  {#if !document.isDocumentFailed}
    {#if document.txidPublished}
      <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#22C55E] text-lg text-white">
        <i class="fa-solid fa-check" />
      </div>

      <div class="w-2/5 text-base font-medium sm:w-3/5 lg:w-2/5">
        <h1 class="truncate">
          Document of <strong>{document.recName}</strong>
          Published
        </h1>
      </div>

      <div class="ml-auto flex gap-6 px-6 md:gap-3 lg:px-0">
        <a name="tooltip" id="Open URL" tooltip-position="top" href={document.url} target="_blank" class="flex items-center">
          <span>
            <i class="fa-solid fa-arrow-up-right-from-square" />
          </span>
          <span class="hidden text-sm font-bold lg:block">&nbsp;&nbsp;Open&nbsp;URL&nbsp;</span>
        </a>
        <div class="relative">
          <button name="tooltip" id="Copy URL" tooltip-position="top" on:click={copyURL} type="button" class="flex items-center">
            <span>
              <i class="fa-solid fa-copy" />
            </span>
            <span class="hidden text-sm font-bold lg:block">&nbsp;&nbsp;Copy&nbsp;URL&nbsp;</span>
          </button>
          <div class="absolute left-[20%] rounded-md bg-black px-3 py-1 {popover ? 'animate-copy' : 'hidden'}">
            <h1 class="text-sm font-medium text-white">Copied</h1>
          </div>
        </div>
      </div>
    {:else}
      <div class="relative flex h-12 w-12 items-center justify-center">
        <div style="border-color: {textColor};color:{textColor}" class="absolute flex h-10 w-10 animate-spin rounded-full border-t-[3px] bg-transparent" />
        <div>
          <i class="fa-solid fa-file" />
        </div>
      </div>

      <h1 class="text-base font-bold">{cardText}</h1>
    {/if}
  {:else}
    {#if showLoading}
      <Loader>
        <span class="flex">
          {@html "Releasing Document&nbsp;&nbsp;"}
        </span>
      </Loader>
    {/if}

    <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-500 text-lg text-white">
      <i class="fa-solid fa-triangle-exclamation" />
    </div>

    <div class="flex w-2/5 items-center gap-3 text-base font-medium">
      <h1 class="truncate">
        Document of <strong class="text-[#491217]">{document.recName}</strong>
        failed
      </h1>
      <button data-tooltip={document.errorMsg} tooltip-position="top">
        <i class="fa-solid fa-circle-info fa-lg text-[#491217]" />
      </button>
    </div>

    <div class="ml-auto flex gap-6 md:gap-3">
      <button disabled={disableReleaseBtn} type="button" on:click={releaseDocument} class="flex items-center rounded-md border border-red-500 p-1 disabled:cursor-not-allowed">
        <span>
          <i class="fa-solid fa-trash-can" />
        </span>
        <span class="text-sm font-bold">&nbsp;&nbsp;Release&nbsp;Document&nbsp;</span>
      </button>
    </div>
  {/if}
</div>
